import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import NameSchema from "_shared/names/name-schema";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfoSchema, {
  RegistrationInfoJSON,
} from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import UserEntityInfo from "users/information/entity-information/user-entity-info";
import UserEntityInfoSchema, { UserEntityInfoJSON } from "users/information/entity-information/user-entity-info-schema";
import { email, z } from "zod";

export type UserEntityMemberInvitationJSON = z.infer<
  typeof UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ
>;
export type UserEntityMemberInvitationParams = z.infer<
  typeof UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ
>;

export default class UserEntityMemberInvitationSchema {
  public static readonly UserEntityMemberInvitationJSONZ = z.object({
    id: GuidSchema.GuidJSONZ.nullish(),
    legalEntityId: GuidSchema.GuidJSONZ,
    legalEntityName: EntityNameSchema.EntityNameJSONZ,
    name: NameSchema.NameJSONZ,
    email: email(),
    status: z.enum(InvitationStatus).nullish(),
    expires: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly UserEntityMemberInvitationZ = z.object({
    id: z.instanceof(Guid).optional(),
    userEntityInfo: z.instanceof(UserEntityInfo),
    registrationInfo: z.instanceof(RegistrationInfo),
    status: z.enum(InvitationStatus).optional(),
    expires: z.instanceof(AHDate).optional(),
  });

  public static validate(values: unknown): UserEntityMemberInvitationParams {
    return parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ, values, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserEntityMemberInvitationJSON {
    return parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserEntityMemberInvitationParams {
    try {
      UserEntityMemberInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserEntityMemberInvitationJSON {
    try {
      UserEntityMemberInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): UserEntityMemberInvitationParams {
    const parsed = parseOrThrow(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "UserEntityMemberInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserEntityMemberInvitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserEntityMemberInvitationParams): UserEntityMemberInvitationJSON {
    const validated = UserEntityMemberInvitationSchema.validate(legalEntity);
    return UserEntityMemberInvitationSchema.codec.encode(validated);
  }

  private static readonly codec = z.codec(
    UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ,
    UserEntityMemberInvitationSchema.UserEntityMemberInvitationZ,
    {
      decode: (json) =>
        ({
          id: new Guid(GuidSchema.deserialize(json.id)),
          userEntityInfo: UserEntityInfo.create(
            UserEntityInfoSchema.deserialize({
              ...json,
              isOfficer: false,
              entityId: json.legalEntityId,
              entityName: json.legalEntityName,
            } satisfies UserEntityInfoJSON),
          ),
          registrationInfo: new RegistrationInfo(
            RegistrationInfoSchema.deserialize({
              ...json,
              name: {
                firstName: json.name.firstName,
                lastName: json.name.lastName,
              },
              email: json.email!,
            } satisfies RegistrationInfoJSON),
          ),
          status: json.status ?? undefined,
        }) satisfies UserEntityMemberInvitationParams,
      encode: (params) => ({
        id: params.id?.toJSON(),
        legalEntityId: params.userEntityInfo.id.toJSON(),
        legalEntityName: params.userEntityInfo.entityName.toJSON(),
        name: {
          firstName: params.registrationInfo.name.firstName,
          lastName: params.registrationInfo.name.lastName,
        },
        email: params.registrationInfo.email.toJSON(),
        expires: params.expires?.toISOString(),
        status: params.status,
      }),
    },
  );
}
